import { Suspense, lazy, useState } from "react";
import { Box, CircleHelp } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/ui-components/ui/dialog";
import { useEntitlement } from "@/contexts/EntitlementContext";
import { UpgradeBody } from "@/components/billing/Upgrade";
import { SkillsEducation, SkillsEducationContent } from "./SkillsEducation";

// Lazy-loaded so the full skills workspace stays out of the chunk for non-Pro
// viewers, who never render it.
const SkillsWorkspace = lazy(() =>
  import("@/components/skills/SkillsWorkspace").then((m) => ({
    default: m.SkillsWorkspace,
  })),
);

/**
 * Route element for /skills. Entitled users get the full SkillsWorkspace (with
 * its library + run-activity tabs). Unentitled users get ONLY an upgrade CTA
 * and the dismissable education area — no skills content.
 *
 * Entitlement is checked after it has resolved (loading → render nothing, avoiding
 * a flash of the upgrade panel for entitled users while the cached fetch loads).
 */
export function SkillsGate() {
  const { can, loading } = useEntitlement();
  const [eduOpen, setEduOpen] = useState(false);

  if (loading) return null;
  if (can("skills")) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <SkillsWorkspace />
      </Suspense>
    );
  }

  // Unentitled: upgrade CTA + education only.
  return (
    <div className="min-h-full">
      <div className="mx-auto max-w-3xl px-6 py-8">
        <div className="flex items-start gap-3">
          <Box size={20} className="mt-0.5 text-brand flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold tracking-tight text-foreground text-balance">
                Turn your sessions into skills
              </h2>
              {/* Always-available help: opens the video + docs education panel in a
                  dialog so it doesn't consume page space. */}
              <button
                type="button"
                onClick={() => setEduOpen(true)}
                aria-label="How skills work"
                title="How skills work"
                className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              >
                <CircleHelp className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              SpecStory Cloud mines your synced coding-agent sessions, judges
              candidate skills cross-vendor, and surfaces the confirmed ones for
              you to install into Claude Code, Codex, Cursor, Antigravity,
              DeepSeek, Droid, and more.
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-lg border border-border bg-card p-4 shadow-sm">
          <UpgradeBody feature="skills" />
        </div>

        <SkillsEducation />
      </div>

      {/* Education dialog: opened from the help icon in the header. */}
      <Dialog open={eduOpen} onOpenChange={(o) => setEduOpen(o)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader className="sr-only">
            <DialogTitle>How skills work</DialogTitle>
            <DialogDescription>
              Learn how skills are mined from your sessions with a short video
              and the docs.
            </DialogDescription>
          </DialogHeader>
          <SkillsEducationContent />
        </DialogContent>
      </Dialog>
    </div>
  );
}
